import { STATUS_MESSAGES, TIME_THRESHOLD_MS } from './constants';

/**
 * Classification rules for trailing documents, shared by the history table and the
 * per-file detail view.
 *
 * These live outside both components because they had drifted apart: the table and
 * the detail view disagreed on whether a given trailing document had failed, so a
 * row could report Successful while the file listed underneath it reported a
 * failure. One definition, two callers.
 *
 * A trailing document is recorded in one of three buckets (`inProcess`,
 * `processed`, `failed`). The bucket is the authoritative outcome; the status
 * message refines it, because a failure can be recorded without the file being
 * moved.
 */

/** A trailing document entry as stored on a history record. */
export interface TrailingDocEntry {
  statusMessage?: string;
  updatedAt?: number | string;
}

/** The three buckets, as stored on a history record. */
export interface TrailingDocBuckets {
  inProcess?: Record<string, TrailingDocEntry>;
  processed?: Record<string, TrailingDocEntry>;
  failed?: Record<string, TrailingDocEntry>;
}

function message(entry: TrailingDocEntry | undefined): string {
  return entry?.statusMessage?.toLowerCase() ?? '';
}

function entries(bucket: Record<string, TrailingDocEntry> | undefined): TrailingDocEntry[] {
  return Object.values(bucket ?? {});
}

/**
 * Whether the entry's message reports a failure.
 *
 * Substring match on "fail" to cover the variants the services emit, from
 * "Failed to transfer file" to "Failed to transfer loan file.". Matches how the
 * backend classifies the same field in `updateDocConnectorHistoryEncompassStatus`.
 */
export function readsAsFailure(entry: TrailingDocEntry | undefined): boolean {
  return message(entry).includes('fail');
}

/** Whether the entry's message reports a completed export. */
export function readsAsExported(entry: TrailingDocEntry | undefined): boolean {
  return message(entry).includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED);
}

/**
 * Whether an entry in the inProcess bucket still has work outstanding.
 *
 * Membership in the bucket is what decides. The message is only consulted to rule
 * out entries that have already reached a terminal state and are waiting to be
 * moved.
 *
 * Deliberately not a whitelist of in-flight messages. That was the previous
 * approach and it left any other wording in no category at all: not pending, not
 * failed, not transmitted, yet still counted in processed. The writers use several
 * messages across four repos and they are free to add more, so an entry the UI does
 * not recognise has to default to "still working" rather than disappearing.
 */
export function isInFlight(entry: TrailingDocEntry | undefined): boolean {
  return !readsAsExported(entry) && !readsAsFailure(entry);
}

/**
 * The phase an in-flight entry is in, for display.
 *
 * Message-based, because the phase is a detail the message is the only source for.
 * Unrecognised wording falls back to the earlier phase, which is the safer default:
 * it points support at Encompass retrieval rather than claiming the destination
 * already has the file.
 */
export function hasReachedTransfer(entry: TrailingDocEntry | undefined): boolean {
  return message(entry).includes(STATUS_MESSAGES.SENDING_FILE);
}

/**
 * Whether an in-flight entry has stopped moving and should be treated as failed.
 *
 * Measured against the entry's own `updatedAt`, which the backend refreshes on
 * every state change. `fallbackDate` is the loan-level record date and is only a
 * fallback: a trailing run fires long after the initial run, so judging a freshly
 * written trailing document by the loan record's age reports it as failed the
 * moment it starts.
 *
 * An entry with no usable timestamp anywhere is treated as stalled, because every
 * entry receives an `updatedAt` when it is moved.
 */
export function isStalled(
  entry: TrailingDocEntry | undefined,
  fallbackDate: number | string | undefined
): boolean {
  if (!isInFlight(entry)) {
    return false;
  }

  const raw = entry?.updatedAt ?? fallbackDate;
  const ms = typeof raw === 'string' ? new Date(raw).getTime() : Number(raw);
  if (raw === undefined || raw === null || isNaN(ms)) {
    return true;
  }

  return Date.now() - ms > TIME_THRESHOLD_MS;
}

/** Total trailing documents across all three buckets. */
export function countProcessed(buckets: TrailingDocBuckets | undefined): number {
  if (!buckets) {
    return 0;
  }
  return entries(buckets.failed).length
    + entries(buckets.inProcess).length
    + entries(buckets.processed).length;
}

/**
 * Trailing documents confirmed delivered.
 *
 * Reads both `inProcess` and `processed`, since an entry can report a completed
 * export before it is moved.
 */
export function countTransmitted(buckets: TrailingDocBuckets | undefined): number {
  if (!buckets) {
    return 0;
  }
  return [...entries(buckets.inProcess), ...entries(buckets.processed)]
    .filter(readsAsExported)
    .length;
}

/** Trailing documents still working and inside the transfer window. */
export function countPending(
  buckets: TrailingDocBuckets | undefined,
  fallbackDate: number | string | undefined
): number {
  return activeInProcess(buckets, fallbackDate).length;
}

/**
 * inProcess entries still working and inside the transfer window.
 *
 * The basis for both the pending tally and the reported phase, so the two cannot
 * disagree about whether a run is active.
 */
export function activeInProcess(
  buckets: TrailingDocBuckets | undefined,
  fallbackDate: number | string | undefined
): TrailingDocEntry[] {
  if (!buckets) {
    return [];
  }
  return entries(buckets.inProcess)
    .filter((entry) => isInFlight(entry) && !isStalled(entry, fallbackDate));
}

/**
 * Trailing documents that failed.
 *
 * Everything in `failed` counts, whatever its message. Entries in the other two
 * buckets count when they report a failure, and `inProcess` entries also count once
 * they stop moving, because a trailing document that stalls is a failure rather
 * than indefinite progress.
 *
 * Disjoint from countPending by construction, so every in-flight entry is counted
 * exactly once across the two.
 */
export function countFailed(
  buckets: TrailingDocBuckets | undefined,
  fallbackDate: number | string | undefined
): number {
  if (!buckets) {
    return 0;
  }

  return entries(buckets.failed).length
    + entries(buckets.processed).filter(readsAsFailure).length
    + entries(buckets.inProcess)
      .filter((entry) => readsAsFailure(entry) || isStalled(entry, fallbackDate))
      .length;
}
